import React, { useRef, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';

const QrGenerator = () => {
  const [urlInput, setUrlInput] = useState('');
  const [qrValue, setQrValue] = useState('');
  const [error, setError] = useState('');
  const canvasRef = useRef(null);

  const normalizeUrl = (rawUrl) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return new URL(trimmed).toString();
    } catch (_) {
      try {
        return new URL(`https://${trimmed}`).toString();
      } catch (_) {
        return null;
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const normalized = normalizeUrl(urlInput);

    if (!normalized) {
      setError('Enter a valid URL (example: https://example.com).');
      setQrValue('');
      return;
    }

    setError('');
    setQrValue(normalized);
  };

  const handleDownload = () => {
    if (!qrValue || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const pngUrl = canvas.toDataURL('image/png');
    const downloadLink = document.createElement('a');

    let safeFileName = 'qr-code';
    try {
      const hostname = new URL(qrValue).hostname.replace(/[^a-z0-9-]+/gi, '-');
      safeFileName = hostname || safeFileName;
    } catch (_) {}

    downloadLink.href = pngUrl;
    downloadLink.download = `${safeFileName}.png`;
    downloadLink.click();
  };

  const handleInputChange = (event) => {
    setUrlInput(event.target.value);
    if (error) {
      setError('');
    }
  };

  return (
    <Box sx={{ maxWidth: 780, margin: '0 auto', padding: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h5" gutterBottom>
            QR Code Generator
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Drop in a URL to create a single-page QR code you can download and share.
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="URL"
              value={urlInput}
              onChange={handleInputChange}
              placeholder="https://www.jurassicquest.com/events/PKCZ14"
              error={Boolean(error)}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button type="submit" variant="contained" sx={{ minWidth: 160 }}>
                Generate QR
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownload}
                disabled={!qrValue}
                sx={{ minWidth: 160 }}
              >
                Download PNG
              </Button>
            </Stack>
          </Stack>
        </Box>

        {qrValue && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              alignSelf: 'center',
              textAlign: 'center',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <QRCodeCanvas value={qrValue} size={280} level="H" includeMargin ref={canvasRef} />
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              {qrValue}
            </Typography>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default QrGenerator;
